import { useMemo } from 'react';

/**
 * Custom hook to deduplicate relationships array
 * Prevents showing the same person multiple times even if they have multiple relationship records
 * 
 * @param {Array} relationships - Array of relationship objects from Redux
 * @returns {Array} - Deduplicated array of relationships
 */
export const useDeduplicatedRelationships = (relationships) => {
  return useMemo(() => {
    if (!Array.isArray(relationships) || relationships.length === 0) {
      return [];
    }
    
    const idMap = new Map(); // Track by _id
    const keyMap = new Map(); // Track by semantic key (name + email or uniqueNumber)
    
    for (const rel of relationships) {
      if (!rel || !rel._id) continue;
      
      // Skip if we've already seen this exact _id
      if (idMap.has(rel._id)) {
        continue;
      }
      
      // Create a semantic key for deduplication
      let key;
      if (rel.uniqueNumber) {
        key = `unique_${rel.uniqueNumber}`;
      } else {
        // Normalize name: trim, lowercase, replace multiple spaces with single space
        let name = '';
        if (rel.contractType === "Individual") {
          name = [rel.firstName, rel.middleName, rel.lastName]
            .filter(Boolean)
            .map(n => n.trim())
            .join(' ')
            .toLowerCase()
            .replace(/\s+/g, ' ');
        } else {
          name = (rel.businessName || '').trim().toLowerCase().replace(/\s+/g, ' ');
        }
        
        const email = (rel.email || '').trim().toLowerCase();
        key = name ? (email ? `name_${name}_email_${email}` : `name_${name}`) : `id_${rel._id}`;
      }
      
      // Check if we've seen this semantic key before
      if (keyMap.has(key) && !key.startsWith('id_')) {
        const existing = keyMap.get(key);
        // Prefer the one with uniqueNumber
        if (rel.uniqueNumber && !existing.uniqueNumber) {
          // Replace existing with current
          keyMap.set(key, rel);
          idMap.set(rel._id, rel);
          idMap.delete(existing._id);
        }
        continue;
      }
      
      // New option - add it
      idMap.set(rel._id, rel);
      keyMap.set(key, rel);
    }
    
    return Array.from(idMap.values());
  }, [relationships]);
};

export default useDeduplicatedRelationships;














